<i18n>
{
	"en": {
		"title": "Hendelser",
		"noEvents": "There are no registered events.",
		"type": "Type",
		"date": "Date",
		"user": "User",
		"signature": "Signature",
		"location": "Location",
		"position":"Position",
		"received": "Received",
		"loaded": "Loaded",
		"collected": "Collected",
		"unloaded": "Unloaded",
		"delivered": "Delivered",
		"inbound": "Inbound",
		"outbound": "Outbound",
		"created": "Created",
		"rejected": "Rejected",
		"unsuccessful": "Unsuccessful pickup",
		"requested": "Requested",
		"accepted": "Accepted",
		"appSend": "Sent to Confirm",
		"info": "Information",
		"ediInstructionReceived": "Inbound EDI",
		"ediInstructionSent": "Outbound EDI",
		"ediTrackAndTraceSent" : "Outbound EDI",
		"showEdiEvents" : "EDI events"
	},
	"no": {
		"title": "Hendelser",
		"noEvents": "Det er ingen registrerte hendelser.",
		"type": "Type",
		"date": "Dato",
		"user": "Bruker",
		"signature": "Signatur",
		"location": "Lokasjon",
		"position":"Posisjon",
		"received": "Mottatt",
		"loaded": "Lastet",
		"collected": "Hentet",
		"unloaded": "Losset",
		"delivered": "Levert",
		"inbound": "Inngående",
		"outbound": "Utgående",
		"created": "Opprettet",
		"rejected": "Avslått",
		"unsuccessful": "Bomtur",
		"requested": "Forespurt",
		"accepted": "Godtatt",
		"appSend": "Sendt til Confirm",
		"info": "Informasjon",
		"ediInstructionReceived": "Inngående EDI",
		"ediInstructionSent": "Utgående EDI",
		"ediTrackAndTraceSent" : "Utgående EDI",
		"showEdiEvents" : "EDI hendelser"
	}
}
</i18n>
<template>
	<div class="log">
		<div class="log-header">
			<t-panel-header
				:text="$t('title')"
				icon="history"
				type="bp"
				size="md"
			/>
			<t-checkbox
				v-model="showEdiMessages"
				:checked="showEdiMessages"
				:label="$t('showEdiEvents')"
				@change="negotiateShowEdiEvents"
			>test</t-checkbox>
		</div>
		<t-grid
			v-if="filteredEvents && filteredEvents.length > 0"
			:items="filteredEvents"
			:columnDefinitions="columnDefinitions"
			:defaultColumnDefinition="defaultColumnDefinition"
			:rowHeight="40"
			:showFilters="false"
			:sort="sort"
			class="log-content"
			@rowDoubleClick="showEventDialog"
		>
			<template v-slot:icon="{ item }">
				<t-timpex-icon
					:icon="getIcon(item.type)"
					type="bp"
					color="blue"
					style="padding-left: 3px;"
				/>
			</template>
			<template v-slot:type="{ item }">
				{{$t(item.type)}}
			</template>
			<template v-slot:location="{ displayValue }">
				<div v-if="displayValue" style="height: 16px;">{{ displayValue.name }}</div>
				<div v-if="displayValue" class="grid-cell-sub-line" :class="{'missing-geo-data': !displayValue.latitude || !displayValue.longitude }">{{ displayValue.street }}, {{ displayValue.postalCode }} {{ displayValue.postalPlace }}</div>
			</template>
			<template v-slot:info="{ item }">
				{{ ($i18n.locale==='nb-NO' || $i18n.locale==='nn-NO' || $i18n.locale==='no')
					? (item.info || item.infoEn)
					: (item.infoEn || item.info) }}
			</template>
			<template v-slot:date="{ item }">
				{{getDate(item.date)}}
			</template>
			<template v-slot:clerk="{ displayValue }">
				<div v-if="displayValue" style="height: 16px;">
					<template v-if="displayValue.name">
						{{displayValue.name}}
					</template>
					<template v-else-if="displayValue.email">
						{{displayValue.email}}
					</template>
					<template v-else-if="displayValue.phoneNumber">
						{{displayValue.phoneNumber}}
					</template>
				</div>
				<div v-if="displayValue && displayValue.name" class="grid-cell-sub-line">
					<template v-if="displayValue.email">
						{{displayValue.email}}
					</template>
					<template v-else-if="displayValue.phoneNumber">
						{{displayValue.phoneNumber}}
					</template>
				</div>
			</template>
			<template v-slot:signature="{ item }">
				<Signature
					:url="item.signatureUrl"
					:name="item.signatureName"
				/>
			</template>
			<template v-slot:position="{ item }">
				<t-icon-button
					icon="map-marker"
					type="bp"
					appearance="minimal"
					disableBoxShadow
					:disabled="eventHasNoPosition(item)"
					@click="openPositionInGoogleMaps(item)"
				/>
			</template>
		</t-grid>
		<t-no-content-panel
			v-else
			:text="$t('noEvents')"
			icon="history"
			iconType="bp"
			class="log-content"
			noBetweenGap
		/>
		<EventsDialog
			:event="selectedEvent"
			:isShown="eventDialogShown"
			@onCancel="eventDialogShown = false"
			@onClose="eventDialogShown = false"
		/>
	</div>
</template>

<script>
import moment from 'moment';
import { eventIcon, storageDateFormat } from '@/core';
import EventsDialog from './events/EventsDialog';
import Signature from '@/modules/newOrder/common/Signature';


export default {
	name: 'Events',
	components: { Signature, EventsDialog },
	props: { order: Object, events: Object },
	data() {
		return { defaultColumnDefinition: { resizable: false, },
			showGrid: false,
			sort: [{ field: 'date', direction: 'desc' }],
			showEdiMessages: false,
			eventDialogShown: false,
			selectedEvent: {},
			filteredEvents: [] };
	},
	computed: {
		items() {
			return this.order.events;
		},
		columnDefinitions() {
			return [
				{
					field: 'icon',
					displayName: '',
					width: 50,
				},
				{
					field: 'type',
					displayName: this.$t('type'),
					minWidth: 100,
					width: 100,
					resizable: true,
				},
				{
					field: 'location',
					valueGetter: item => item.location,
					displayName: this.$t('location'),
					minWidth: 70,
					resizable: true,
				},
				{
					field: 'info',
					displayName: this.$t('info'),
					minWidth: 80,
					resizable: true,
				},
				{
					field: 'date',
					displayName: this.$t('date'),
					width: 120,
				},
				{
					field: 'clerk',
					valueGetter: item => item.clerk,
					displayName: this.$t('user'),
					width: 200,
				},
				{
					field: 'signature',
					displayName: this.$t('signature'),
					width: 180,
				},
				{
					field: 'position',
					displayName: this.$t('position'),
					width: 70,
				}
			];
		},
	},

	created() {
		this.filteredEvents = this.getFilteredEvents();
	},
	methods: {
		negotiateShowEdiEvents(showEdiEvents) {
			this.showEdiMessages ===!this.showEdiMessages;
			if (showEdiEvents) {
				this.filteredEvents = this.order.events;
			} else {
				this.filteredEvents = this.getFilteredEvents();
			}
		},
		showEventDialog(selected) {
			this.selectedEvent = this.order.events.find(x => x.id === selected);
			this.eventDialogShown = true;
		},
		getFilteredEvents() {
			return this.order.events.filter(x => !(x.source.includes('ediInbound') || x.source.includes('ediOutbound')));
		},
		onClose() {
			this.showGrid = false;
			this.$emit('close');
		},
		getIcon(type) {
			return eventIcon.getIcon(type);
		},
		getDate(date) {
			return moment(date.date, storageDateFormat).format('DD.MM.YYYY HH:mm');
		},
		openPositionInGoogleMaps(item) {
			let url = `https://www.google.com/maps/search/?api=1&query=${item.latitude},${item.longitude}`;
			let win = window.open(url, '_blank');
			win.focus();
		},
		eventHasNoPosition(item) {
			return !item.latitude || !item.longitude;
		}
	}
};
</script>

<style lang="scss" scoped>
.log {
	height: 100%;
	min-height: inherit;
}

.log-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 15px;
	background: white;
}

.log-content {
	height: calc(100% - 60px);
	width: 100%;
	min-height: inherit;
}
</style>
